import type { FeatureEvents, OperationalEvent, ScreenEvent, UIEvent } from '../new-analytics-types';
import { Action, ActionSubject, CapabilitySubject, OperationalAction } from '../new-analytics-types';

type ScreenKeys =
  | 'briePoliciesDashboardEmptyScreen'
  | 'briePoliciesDashboardScreen'
  | 'briePolicyDetailsScreen'
  | 'brieBackupDetailsSidePanelScreen'
  | 'brieCreatePolicyScreen'
  | 'brieEditPolicyScreen'
  | 'brieSelectAppScreen'
  | 'brieSelectScheduleScreen'
  | 'brieSelectAtlassianStorageScreen'
  | 'brieSelectAmazonBucketScreen'
  | 'brieIntroduceV2UIModal'
  | 'brieSetWeeklyScheduleModal'
  | 'brieAutomatedRestoresUIModal'
  | 'brieSandboxScheduleWarningMessage'
  | 'brieGracePeriodModal'
  | 'brieGracePeriodOverModal'
  | 'briePnpPopup'

  // Restore flow screen
  | 'brieRestoreSelectAppScreen'
  | 'brieRestoreSelectBackupScreen'
  | 'brieRestoreSelectDataScreen'
  | 'brieRestoreSelectDestinationScreen'
  | 'brieRestoreValidateDataScreen'
  | 'brieRestoreReviewRestoreScreen';

type UiKeys =
  | 'createNewPolicyClicked'
  | 'restoreBackupClicked'
  | 'policiesSearchField'
  | 'backupsSearchField'
  | 'viewBackupsClicked'
  | 'editPolicyClicked'
  | 'activatePolicyClicked'
  | 'downloadReportClicked'
  | 'connectStorageClicked'
  | 'viewBackupDetailsClicked'
  | 'updatePolicyClicked'
  | 'selectAppClicked'
  | 'appSelectedClicked'
  | 'setScheduleClicked'
  | 'scheduleSelectedClicked'
  | 'storageSelectedClicked'
  | 'setStorageClicked'
  | 'connectNewBucketClicked'
  | 'takeTheTourClicked'
  | 'setWeeklyScheduleClicked'
  | 'dismissWeeklyScheduleClicked'
  | 'toggleEntitiesClicked'
  | 'removeScheduleClicked'
  | 'restoreFlowNextClicked'
  | 'restoreFlowBackClicked'
  | 'sitesSearchField'
  | 'externalLinkClicked'
  | 'addAppClicked'
  | 'automateRestoresClicked'
  | 'dismissSandboxScheduleWarningClicked'
  | 'dismissAutomateRestoresClicked'
  | 'pricingContactSalesClicked';

type OperationalKeys =
  | 'brieScheduleRestoreSuccess'
  | 'brieScheduleRestoreFailed'
  | 'brieStartRestoreSuccess'
  | 'brieStartRestoreFailed'
  | 'brieImportStorageSuccess'
  | 'brieImportStorageFailed';

export enum BrieV2Screens {
  briePoliciesDashboardEmptyScreen = 'briePoliciesDashboardEmptyScreen',
  briePoliciesDashboardScreen = 'briePoliciesDashboardScreen',
  briePolicyDetailsScreen = 'briePolicyDetailsScreen',
  brieBackupDetailsSidePanelScreen = 'brieBackupDetailsSidePanelScreen',
  brieCreatePolicyScreen = 'brieCreatePolicyScreen',
  brieEditPolicyScreen = 'brieEditPolicyScreen',
  brieSelectAppScreen = 'brieSelectAppScreen',
  brieSelectScheduleScreen = 'brieSelectScheduleScreen',
  brieSelectAtlassianStorageScreen = 'brieSelectAtlassianStorageScreen',
  brieSelectAmazonBucketScreen = 'brieSelectAmazonBucketScreen',
  brieIntroduceV2UIModal = 'brieIntroduceV2UIModal',
  brieSetWeeklyScheduleModal = 'brieSetWeeklyScheduleModal',
  brieAutomatedRestoresUIModal = 'brieAutomatedRestoresUIModal',
  brieSandboxScheduleWarningMessage = 'brieSandboxScheduleWarningMessage',
  brieGracePeriodModal = 'brieGracePeriodModal',
  brieGracePeriodOverModal = 'brieGracePeriodOverModal',
  briePnpPopup = 'briePnpPopup',
  // Restore flow screen
  brieRestoreSelectAppScreen = 'brieRestoreSelectAppScreen',
  brieRestoreSelectBackupScreen = 'brieRestoreSelectBackupScreen',
  brieRestoreSelectDataScreen = 'brieRestoreSelectDataScreen',
  brieRestoreSelectDestinationScreen = 'brieRestoreSelectDestinationScreen',
  brieRestoreValidateDataScreen = 'brieRestoreValidateDataScreen',
  brieRestoreReviewRestoreScreen = 'brieRestoreReviewRestoreScreen',
}

export const brieV2Events: FeatureEvents<ScreenKeys, UiKeys, string, OperationalKeys> = {
  confluenceSpec: 'https://hello.atlassian.net/wiki/spaces/CLOUDADMIN/pages/3160450379/BRIE+Analytics+Spec',
  screen: {
    briePnpPopup: (): ScreenEvent => ({
      data: {
        name: BrieV2Screens.briePnpPopup,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/97778',
    }),
    briePoliciesDashboardEmptyScreen: (attributes: { isOpenBetaCustomer: boolean; withinGuardrail: boolean }): ScreenEvent => ({
      data: {
        name: BrieV2Screens.briePoliciesDashboardEmptyScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/82052',
    }),
    briePoliciesDashboardScreen: (attributes: {
      dashboard: string;
      isOpenBetaCustomer: boolean;
      withinGuardrail: boolean;
    }): ScreenEvent => ({
      data: {
        name: BrieV2Screens.briePoliciesDashboardScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/82053',
    }),
    briePolicyDetailsScreen: (): ScreenEvent => ({
      data: {
        name: BrieV2Screens.briePolicyDetailsScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/82055',
    }),
    brieBackupDetailsSidePanelScreen: (): ScreenEvent => ({
      data: {
        name: BrieV2Screens.brieBackupDetailsSidePanelScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/82143',
    }),
    brieCreatePolicyScreen: (): ScreenEvent => ({
      data: {
        name: BrieV2Screens.brieCreatePolicyScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/82210',
    }),
    brieEditPolicyScreen: (): ScreenEvent => ({
      data: {
        name: BrieV2Screens.brieEditPolicyScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/82211',
    }),
    brieSelectAppScreen: (): ScreenEvent => ({
      data: {
        name: BrieV2Screens.brieSelectAppScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/82212',
    }),
    brieSelectScheduleScreen: (): ScreenEvent => ({
      data: {
        name: BrieV2Screens.brieSelectScheduleScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/82213',
    }),
    brieSelectAtlassianStorageScreen: (): ScreenEvent => ({
      data: {
        name: BrieV2Screens.brieSelectAtlassianStorageScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/82214',
    }),
    brieSelectAmazonBucketScreen: (attributes: { stepName: string }): ScreenEvent => ({
      data: {
        name: BrieV2Screens.brieSelectAmazonBucketScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/82215',
    }),
    brieIntroduceV2UIModal: (): ScreenEvent => ({
      data: {
        name: BrieV2Screens.brieIntroduceV2UIModal,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/85378',
    }),
    brieAutomatedRestoresUIModal: (): ScreenEvent => ({
      data: {
        name: BrieV2Screens.brieAutomatedRestoresUIModal,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/86983',
    }),
    brieSetWeeklyScheduleModal: (): ScreenEvent => ({
      data: {
        name: BrieV2Screens.brieSetWeeklyScheduleModal,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/85379',
    }),
    brieGracePeriodModal: (): ScreenEvent => ({
      data: {
        name: BrieV2Screens.brieGracePeriodModal,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/95783',
    }),
    brieGracePeriodOverModal: (): ScreenEvent => ({
      data: {
        name: BrieV2Screens.brieGracePeriodOverModal,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/95784',
    }),
    brieRestoreSelectAppScreen: (): ScreenEvent => ({
      data: {
        name: BrieV2Screens.brieRestoreSelectAppScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/87259',
    }),
    brieRestoreSelectBackupScreen: (attributes): ScreenEvent => ({
      data: {
        name: BrieV2Screens.brieRestoreSelectBackupScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/87260',
    }),
    brieRestoreSelectDataScreen: (attributes): ScreenEvent => ({
      data: {
        name: BrieV2Screens.brieRestoreSelectDataScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/87261',
    }),
    brieRestoreSelectDestinationScreen: (): ScreenEvent => ({
      data: {
        name: BrieV2Screens.brieRestoreSelectDestinationScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/87262',
    }),
    brieRestoreValidateDataScreen: (attributes): ScreenEvent => ({
      data: {
        name: BrieV2Screens.brieRestoreValidateDataScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/87263',
    }),
    brieRestoreReviewRestoreScreen: (): ScreenEvent => ({
      data: {
        name: BrieV2Screens.brieRestoreReviewRestoreScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/87264',
    }),
    brieSandboxScheduleWarningMessage: (): ScreenEvent => ({
      data: {
        name: BrieV2Screens.brieSandboxScheduleWarningMessage,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/95814',
    }),
  },

  ui: {
    createNewPolicyClicked: (): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'createNewPolicy',
        source: BrieV2Screens.briePoliciesDashboardScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/82056',
    }),
    restoreBackupClicked: ({ source }: { source: BrieV2Screens }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'restoreBackup',
        source,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/82057',
    }),
    policiesSearchField: ({
      attributes,
      source,
    }: {
      attributes: { searchText?: string; storage?: string; status?: string; dashboard?: string };
      source?: BrieV2Screens;
    }): UIEvent => ({
      data: {
        action: Action.Searched,
        actionSubject: ActionSubject.Table,
        actionSubjectId: 'searchingPolicies',
        source: source || BrieV2Screens.briePoliciesDashboardScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/82216',
    }),
    backupsSearchField: ({
      attributes,
      source,
    }: {
      attributes: { date?: string; app?: string; status?: string };
      source?: BrieV2Screens;
    }): UIEvent => ({
      data: {
        action: Action.Searched,
        actionSubject: ActionSubject.Table,
        actionSubjectId: 'searchingBackups',
        source: source || BrieV2Screens.briePolicyDetailsScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/82217',
    }),
    viewBackupsClicked: ({ attributes }: { attributes: { policyId: string; schedule: string; storage: string } }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'viewBackups',
        source: BrieV2Screens.briePoliciesDashboardScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/82060',
    }),
    editPolicyClicked: ({
      source,
      attributes,
    }: {
      source: BrieV2Screens;
      attributes: { policyId: string; schedule: string; storage: string };
    }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'editPolicy',
        source,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/69522',
    }),
    activatePolicyClicked: ({ source }: { source: BrieV2Screens }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'activatePolicy',
        source,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/82061',
    }),
    downloadReportClicked: ({ source }: { source: BrieV2Screens }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'downloadReport',
        source,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/82062',
    }),
    connectStorageClicked: ({ source }: { source: BrieV2Screens }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'connectStorage',
        source,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/82063',
    }),
    viewBackupDetailsClicked: (): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'viewBackupDetails',
        source: BrieV2Screens.briePolicyDetailsScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/82144',
    }),
    updatePolicyClicked: (): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'updatePolicy',
        source: BrieV2Screens.brieEditPolicyScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/82228',
    }),
    selectAppClicked: ({ source }: { source: BrieV2Screens }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'selectApp',
        source,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/82218',
    }),
    appSelectedClicked: (): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'appSelected',
        source: BrieV2Screens.briePolicyDetailsScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/82219',
    }),
    setScheduleClicked: ({ source }: { source: BrieV2Screens }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'setSchedule',
        source,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/82220',
    }),
    scheduleSelectedClicked: ({ attributes }: { attributes: { isPolicyActive?: boolean; interval?: string } }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'scheduleSelected',
        source: BrieV2Screens.brieSelectScheduleScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/82221',
    }),
    storageSelectedClicked: ({ source, attributes }: { source: BrieV2Screens; attributes: { strorageType?: string } }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'storageSelected',
        source,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/82222',
    }),
    setStorageClicked: ({ source, attributes }: { source: BrieV2Screens; attributes: { strorageType?: string } }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'setStorage',
        source,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/82223',
    }),
    connectNewBucketClicked: ({ source }: { source: BrieV2Screens }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'connectNewBucket',
        source,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/82224',
    }),
    takeTheTourClicked: (): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'takeTheTour',
        source: BrieV2Screens.brieIntroduceV2UIModal,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/85390',
    }),
    setWeeklyScheduleClicked: ({ source }: { source: BrieV2Screens }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'setWeeklySchedule',
        source,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/85389',
    }),
    dismissWeeklyScheduleClicked: ({ source }: { source: BrieV2Screens }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'dismissWeeklySchedule',
        source,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/85388',
    }),
    toggleEntitiesClicked: ({
      source,
      attributes,
    }: {
      source: BrieV2Screens;
      attributes: { entityName?: string; isSelected?: boolean };
    }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'toggleEntities',
        source,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/85387',
    }),
    removeScheduleClicked: ({
      source,
      attributes,
    }: {
      source: BrieV2Screens;
      attributes: { isPolicyActive?: boolean; interval?: string };
    }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'removeSchedule',
        source,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/85386',
    }),
    restoreFlowNextClicked: ({ source }: { source: BrieV2Screens }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'restoreFlowNext',
        source,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/87267',
    }),
    restoreFlowBackClicked: ({ source }: { source: BrieV2Screens }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'restoreFlowBack',
        source,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/87268',
    }),
    sitesSearchField: ({
      attributes,
      source,
    }: {
      attributes: { searchText?: string; shardFilterSelected?: boolean };
      source: BrieV2Screens;
    }): UIEvent => ({
      data: {
        action: Action.Searched,
        actionSubject: ActionSubject.Table,
        actionSubjectId: 'searchingSites',
        source,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/87269',
    }),
    externalLinkClicked: ({
      attributes,
      source,
    }: {
      attributes: { linkText: string; linkUrl: string; fromSubject?: string };
      source: BrieV2Screens;
    }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Link,
        actionSubjectId: 'externalLink',
        source,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/87278',
    }),
    addAppClicked: (): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'addApp',
        source: BrieV2Screens.brieRestoreSelectDestinationScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/87474',
    }),
    automateRestoresClicked: ({ attributes, source }: { attributes: { status: string }; source: BrieV2Screens }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'automateRestores',
        source,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/87532',
    }),
    dismissSandboxScheduleWarningClicked: (): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'dismissSandboxScheduleWarning',
        source: BrieV2Screens.briePoliciesDashboardScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/95813',
    }),
    dismissAutomateRestoresClicked: ({ source }: { source: BrieV2Screens }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'dismissAutomateRestores',
        source,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/87534',
    }),
    pricingContactSalesClicked: ({ attributes, source }: { attributes: { fromSubject: string }; source: BrieV2Screens }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'briePnPContactSales',
        source,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/95567',
    }),
  },

  track: {},

  operational: {
    brieScheduleRestoreSuccess: (attributes): OperationalEvent => ({
      data: {
        action: OperationalAction.Succeeded,
        actionSubject: CapabilitySubject.ScheduleRestore,
        source: BrieV2Screens.brieRestoreReviewRestoreScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/87270',
    }),
    brieScheduleRestoreFailed: (attributes): OperationalEvent => ({
      data: {
        action: OperationalAction.Failed,
        actionSubject: CapabilitySubject.ScheduleRestore,
        source: BrieV2Screens.brieRestoreReviewRestoreScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/87271',
    }),
    brieStartRestoreSuccess: (attributes): OperationalEvent => ({
      data: {
        action: OperationalAction.Succeeded,
        actionSubject: CapabilitySubject.StartRestore,
        source: BrieV2Screens.brieRestoreReviewRestoreScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/87272',
    }),
    brieStartRestoreFailed: (attributes): OperationalEvent => ({
      data: {
        action: OperationalAction.Failed,
        actionSubject: CapabilitySubject.StartRestore,
        source: BrieV2Screens.brieRestoreReviewRestoreScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/87275',
    }),
    brieImportStorageSuccess: (attributes): OperationalEvent => ({
      data: {
        action: OperationalAction.Succeeded,
        actionSubject: CapabilitySubject.ImportStorage,
        source: BrieV2Screens.brieRestoreReviewRestoreScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/87273',
    }),
    brieImportStorageFailed: (attributes): OperationalEvent => ({
      data: {
        action: OperationalAction.Failed,
        actionSubject: CapabilitySubject.ImportStorage,
        source: BrieV2Screens.brieRestoreReviewRestoreScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/87274',
    }),
  },
};
